import React, { useEffect, useState } from "react";
import { Panel, Field } from "@/pages/admin/shared";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Plus, Trash2, Save, ArrowUp, ArrowDown, Pencil } from "lucide-react";
import { toast } from "sonner";
import api from "@/pages/admin/api";

export default function FaqManager() {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({ question: "", answer: "", order: 0 });
  const [editing, setEditing] = useState(null);

  const load = () => api.get("/faq").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const submit = async () => {
    if (!form.question || !form.answer) return toast.error("Question and answer required");
    try {
      if (editing) {
        await api.put(`/faq/${editing}`, form);
        toast.success("FAQ updated");
      } else {
        await api.post("/faq", form);
        toast.success("FAQ added");
      }
      setForm({ question: "", answer: "", order: 0 });
      setEditing(null);
      load();
    } catch { toast.error("Save failed"); }
  };

  const edit = (f) => { setEditing(f.id); setForm({ question: f.question, answer: f.answer, order: f.order }); };
  const del = async (id) => {
    if (!window.confirm("Delete FAQ?")) return;
    await api.delete(`/faq/${id}`);
    load();
  };

  const move = async (idx, dir) => {
    const next = [...items];
    const j = idx + dir;
    if (j < 0 || j >= next.length) return;
    [next[idx], next[j]] = [next[j], next[idx]];
    const orders = next.map((f, i) => ({ id: f.id, order: i + 1 }));
    setItems(next.map((f, i) => ({ ...f, order: i + 1 })));
    await api.put("/faq/reorder", orders);
  };

  return (
    <div className="grid lg:grid-cols-2 gap-6">
      <Panel title={editing ? "Edit FAQ" : "Add FAQ"} actions={editing && <Button variant="outline" onClick={() => { setEditing(null); setForm({ question: "", answer: "", order: 0 }); }} className="rounded-xl">Cancel</Button>}>
        <div className="space-y-3">
          <Field label="Question"><Input data-testid="faq-question" value={form.question} onChange={(e) => setForm({ ...form, question: e.target.value })} /></Field>
          <Field label="Answer"><Textarea data-testid="faq-answer" rows={5} value={form.answer} onChange={(e) => setForm({ ...form, answer: e.target.value })} /></Field>
          <Field label="Order"><Input type="number" value={form.order} onChange={(e) => setForm({ ...form, order: Number(e.target.value) })} /></Field>
          <Button data-testid="faq-submit" onClick={submit} className="rounded-xl bg-blue-600 hover:bg-blue-700 text-white">
            {editing ? <><Save size={14} className="mr-1" /> Update</> : <><Plus size={14} className="mr-1" /> Add FAQ</>}
          </Button>
        </div>
      </Panel>

      <Panel title={`Existing FAQs (${items.length})`}>
        <div className="space-y-2 max-h-[600px] overflow-y-auto">
          {items.map((f, i) => (
            <div key={f.id} className="rounded-xl border border-border p-3">
              <div className="flex items-start gap-2">
                <div className="flex flex-col shrink-0">
                  <button onClick={() => move(i, -1)} disabled={i === 0} className="p-1 hover:bg-accent rounded disabled:opacity-30"><ArrowUp size={12} /></button>
                  <button onClick={() => move(i, 1)} disabled={i === items.length - 1} className="p-1 hover:bg-accent rounded disabled:opacity-30"><ArrowDown size={12} /></button>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium">{f.question}</div>
                  <div className="text-xs text-muted-foreground line-clamp-2">{f.answer}</div>
                </div>
                <button aria-label="Edit" onClick={() => edit(f)} className="text-blue-600 hover:opacity-70 p-1"><Pencil size={13} /></button>
                <button aria-label="Delete" onClick={() => del(f.id)} className="text-red-500 hover:opacity-70 p-1"><Trash2 size={13} /></button>
              </div>
            </div>
          ))}
        </div>
      </Panel>
    </div>
  );
}
